import React, { ReactNode, useContext } from 'react';
import styled from 'styled-components';

import { useQuestsKratesMetadata } from '#/api/queries/questsKratesQueries';
import MaterialSymbol from '#/components/basic/icons/MaterialSymbol';
import { CloseHeaderDropdownContext } from '#/components/layout/header/closeHeaderDropdownContext';
import {
  COMPETITION_ENABLED,
  MARKET_MAKING_REWARDS_ENABLED,
  REFERRALS_ENABLED,
  POINTS_ENABLED,
  MILESTONE_COMPETITION_ENABLED,
  BUILDER_REWARDS_ENABLED,
  urls,
} from '#/config/constants';
import { formatNumber } from '#/lib/display/formatting';
import { isRelativeLink, openExternalUrl, redirectTo } from '#/lib/redirect';
import { useSelector } from '#/reduxState/storeCore';

import seasonBadge from './images/seasonBadge.png';
import { TESTNET_SANDBOX_COMPETITION_ENABLED } from './testnetSandbox/constants';

const Items = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 4px;
  padding: 4px;

  .isDesktopStyle & {
    padding: 12px;
    /* Keep the hovered box from expanding when Krates metadata numbers load */
    min-width: 422px;
    background: radial-gradient(
      100px 100px at 0% 0%,
      var(--main) 0%,
      rgba(224, 62, 23, 0.01) 100px
    );
  }
`;

const UnicodeIcon = styled.span`
  font-size: 24px;
`;

const KrateBadgeIcon = styled.img`
  height: 31px;
  flex-shrink: 0;
`;

const Separator = styled.div`
  height: 1px;
  background: var(--separator);
  margin: 8px 0;
`;

const LinkBox = styled.div<{ disabled?: boolean }>`
  position: relative;
  padding: 16px 50px 16px 28px;
  min-height: 40px;
  display: flex;
  align-items: center;
  ${({ disabled }) =>
    disabled ? 'opacity: 0.8; filter: grayscale(100%);' : ''}
  border-radius: 4px;

  .isMobileStyle & {
    padding: 32px 50px 32px 28px;
    border-radius: 2px;
    background: var(--elevation-light);
  }

  &:hover {
    background: #02163b55;
    color: #fff;
    cursor: pointer;
  }
`;

const TextWrap = styled.div`
  padding-left: 16px;
  padding-right: 24px;
  .isMobileStyle & {
    padding-right: 4px;
  }
`;

const Heading = styled.h2`
  line-height: 1;
  display: inline-block;
  font-size: var(--medium);
  color: var(--white);
  padding: 0;
  margin: 0;
  .isMobileStyle & {
    font-size: var(--large);
    padding-bottom: 8px;
  }
`;

const Description = styled.div`
  font-size: var(--small);
  color: var(--gray4c);
  line-height: normal;
`;

const ArrowWrap = styled.div`
  position: absolute;
  right: 14px;
  top: calc(50% - 15px);
`;

const ComingSoon = styled.div`
  position: absolute;
  right: 9px;
  top: 9px;
  border-radius: 4px;
  background: var(--black1b);
  padding: 3px 8px 4px 8px;

  color: var(--gray4c);
  font-size: var(--small);
  font-style: normal;
  font-weight: normal;
  line-height: normal;
  text-transform: uppercase;
`;

function RewardItem({
  icon,
  title,
  children,
  path,
  disabled,
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
  path: string;
  disabled?: boolean;
}) {
  const closeDropdown = useContext(CloseHeaderDropdownContext);
  return (
    <LinkBox
      onClick={
        disabled
          ? undefined
          : () => {
              closeDropdown();
              if (isRelativeLink(path)) {
                redirectTo(path);
              } else {
                openExternalUrl(path);
              }
            }
      }
      disabled={disabled}
    >
      {disabled && <ComingSoon>Coming Soon</ComingSoon>}
      <div>
        <UnicodeIcon>{icon}</UnicodeIcon>
      </div>
      <TextWrap>
        <Heading>{title}</Heading>
        <Description>{children}</Description>
      </TextWrap>
      {disabled ? null : (
        <ArrowWrap>
          <MaterialSymbol
            icon="arrow_right"
            fontSize="large"
            color="var(--white)"
            rounded
          />
        </ArrowWrap>
      )}
    </LinkBox>
  );
}

export default function RewardsMenuSection() {
  const isMobile = useSelector(state => state.screen.isMobile);
  const { data: questsKratesMetadata } = useQuestsKratesMetadata();
  return (
    <Items>
      {TESTNET_SANDBOX_COMPETITION_ENABLED || COMPETITION_ENABLED ? (
        <RewardItem
          title="Trading Competition"
          icon="⚡"
          path={
            TESTNET_SANDBOX_COMPETITION_ENABLED
              ? '/rewards/competition-leaderboard'
              : '/rewards/competition'
          }
        >
          Earn rewards just by trading.
        </RewardItem>
      ) : MILESTONE_COMPETITION_ENABLED ? (
        <RewardItem
          title="Trading Competition"
          icon="⚡"
          path="/rewards/competition"
        >
          Earn rewards just by trading.
        </RewardItem>
      ) : null}

      {POINTS_ENABLED && (
        <RewardItem
          title="Points"
          icon="💸"
          // disabled={!POINTS_ENABLED}
          path="/rewards/points"
        >
          Earn points with your activity.
        </RewardItem>
      )}

      {MARKET_MAKING_REWARDS_ENABLED && (
        <RewardItem
          title="Market Making"
          icon="🤝"
          path="/rewards/market-maker"
        >
          Enjoy exclusive rewards based on liquidity.
        </RewardItem>
      )}

      <RewardItem
        title="Referrals"
        icon="📢"
        path="/rewards/referrals"
        disabled={!REFERRALS_ENABLED}
      >
        Refer and earn affiliate fees.
      </RewardItem>

      {BUILDER_REWARDS_ENABLED && (
        <RewardItem title="Builder Rewards" icon="🛠️" path="/rewards/builder">
          Integrate and earn a share of trading fees.
        </RewardItem>
      )}

      {!isMobile && <Separator />}

      <RewardItem
        title="Other Katana Rewards"
        icon={<KrateBadgeIcon src={seasonBadge} alt="" />}
        path={urls.katanaAppQuests}
      >
        {questsKratesMetadata ? (
          <>
            Keep stacking XP · Win $
            {formatNumber(questsKratesMetadata.prizePoolUsd, {
              fixedDecimals: 0,
            })}
            <br />
            Every Krate has a reward. Redeem{' '}
            {formatNumber(questsKratesMetadata.priceXp, {
              fixedDecimals: 0,
            })}{' '}
            XP.
          </>
        ) : (
          <>
            Keep stacking XP
            <br />
            Every Krate has a reward.
          </>
        )}
      </RewardItem>
    </Items>
  );
}
